import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CAD } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const COLORS = ["#7B8C73", "#1A1A1A", "#C7A17A", "#A8B5A0", "#8C7B6B", "#B5A08C"];

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="animate-pulse text-muted-foreground text-sm">Loading analytics…</div>;

  const kpis = [
    { label: "Total Leads", value: data.total_leads },
    { label: "Total Events", value: data.total_events },
    { label: "Conversion Rate", value: `${data.conversion_rate}%` },
    { label: "Avg Quote Value", value: CAD(data.avg_quote_value) },
    { label: "Avg Booked Value", value: CAD(data.avg_booked_value) },
    { label: "Upcoming Revenue", value: CAD(data.upcoming_revenue) },
    { label: "Deposits Collected", value: CAD(data.deposit_totals) },
    { label: "Outstanding Balances", value: CAD(data.outstanding_balances) },
  ];

  return (
    <div className="animate-fade-in">
      <div className="mb-6"><p className="eyebrow mb-2">Insights</p><h1 className="font-serif text-4xl">Analytics</h1></div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((k) => (
          <div key={k.label} className="df-card p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{k.label}</div>
            <div className="font-serif text-3xl">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Revenue by month">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.revenue_by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#999" />
              <YAxis tick={{ fontSize: 11 }} stroke="#999" />
              <Tooltip formatter={(v) => CAD(v)} />
              <Bar dataKey="value" fill="#7B8C73" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Revenue by city">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.revenue_by_city} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#999" />
              <YAxis type="category" dataKey="city" width={80} tick={{ fontSize: 11 }} stroke="#999" />
              <Tooltip formatter={(v) => CAD(v)} />
              <Bar dataKey="value" fill="#1A1A1A" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Most requested cocktails">
          <div className="space-y-3 pt-2">
            {data.most_requested_cocktails.map((c, i) => {
              const max = data.most_requested_cocktails[0]?.count || 1;
              return (
                <div key={c.name}>
                  <div className="flex justify-between text-sm mb-1"><span>{c.name}</span><span className="text-muted-foreground">{c.count}</span></div>
                  <div className="h-2 rounded-full bg-clay overflow-hidden"><div className="h-full bg-olive rounded-full" style={{ width: `${(c.count / max) * 100}%` }} /></div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="Event types & lead sources">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2 text-center">Event types</p>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data.most_common_event_types} dataKey="count" nameKey="type" outerRadius={70}>
                    {data.most_common_event_types.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 self-center">
              {data.lead_sources.map((s) => (
                <div key={s.source} className="flex justify-between text-sm border-b border-line pb-1"><span>{s.source}</span><span className="text-muted-foreground">{s.count}</span></div>
              ))}
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}

const Panel = ({ title, children }) => (
  <div className="df-card p-6"><h2 className="font-serif text-2xl mb-4">{title}</h2>{children}</div>
);
